import { useFormik } from "formik";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { BULK_OPERATION_CREATE_CONFIG } from "../../../constant/data";
import { useNotification } from "../../../hooks/useNotification";
import {
  useLazyDownloadBulkFormatCreateQuery,
  useLazyDownloadBulkFormatUpdateQuery,
  useUploadBulkFormatCreateMutation,
} from "../../../store/apiSlice/ingestApiSlice";
import { ButtonT1 } from "../../atoms/Button";
import { Card } from "../../atoms/card";
import { FormControlError } from "../../atoms/FormFiledError";
import { FormFileInput } from "../../atoms/Input/Input";
import { ModalOnPortal } from "../../atoms/modal";
import { SectionDescription, SectionTitle } from "../../atoms/typography";
import { PortalFooter } from "../../molecules/Footer";
import { PortalHeader } from "../../molecules/Header";
import { ModalOverlay, ModalT1 } from "../../organisms/Modal";
import { ModalBodyT1 } from "../../organisms/Modal/Body";

const BULK_UPDATE_REQUIRES_SELECTION = {
  stock_movement:
    "Select at least one stock line first. This template is pre-filled from your selection with product, warehouse, location, and batch details for the stock you intend to move.",
  stock_reclassification:
    "Select at least one stock line first. This template is pre-filled from your selection with product, warehouse, location, batch, and current status for the stock you intend to reclassify.",
};

export const BulkUploaderForm = ({ module, mode }) => {
  const [upload, { isLoading }] = useUploadBulkFormatCreateMutation();
  const [triggerRedownload, { isFetching: isRedownloading }] =
    useLazyDownloadBulkFormatCreateQuery();

  const notify = useNotification();
  const navigate = useNavigate();

  const [showRedownload, setShowRedownload] = useState(false);

  const initialValues = {
    file: null,
  };

  const validationSchema = Yup.object({
    file: Yup.mixed()
      .required("A file is required.")
      .test("fileType", "Unsupported file format. Please upload only CSV or XLSX files.", file => {
        if (!file) return false;
        const supportedFormats = [
          "text/csv",
          "application/vnd.ms-excel", // .xls (legacy Excel)
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
        ];
        return supportedFormats.includes(file.type);
      }),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async values => {
      try {
        const formData = new FormData();
        formData.append("file", values.file);

        if (mode === "update") {
          formData.append("mode", mode);
        }
        const res = await upload({ module, formData }).unwrap();
        navigate(`/bulk/${module}/${res.id}`);
      } catch (error) {
        const errorData = error.data;
        if (errorData?.file) {
          formik.setFieldError("file", errorData?.file);
          if (errorData?.template_mismatch) {
            setShowRedownload(true);
          }
        } else {
          notify({
            type: "error",
            message:
              errorData?.detail ||
              "Something went wrong while processing the data. Please retry. If the problem persists, reach out to your administrator.",
          });
          formik.resetForm();
        }
      }
    },
  });

  // Clear re-download hint when user picks a new file
  useEffect(() => {
    setShowRedownload(false);
  }, [formik.values.file]);

  const handleRedownload = () => {
    const fileName = `${module}_template_${new Date().toISOString().slice(0, 19).replace(/[-T:]/g, "")}.xlsx`;
    triggerRedownload({ module, fileName });
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormFileInput formik={formik} disabled={isLoading} isLoading={isLoading} name="file" />
      <FormControlError errorMsg={formik.touched.file && formik.errors.file} />
      {showRedownload && (
        <ButtonT1
          $variant="primaryLight"
          $width="100%"
          disabled={isRedownloading}
          loading={isRedownloading}
          loadingText="Preparing..."
          title="Re-download latest template"
          onClickCallback={handleRedownload}
        />
      )}
    </form>
  );
};

export const ModalCreateBulkWrapper = forwardRef(function ModalCreateBulkWrapper({ module }, ref) {
  const [openBulkCreateModal, setOpenBulkCreateModal] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      openBulkCreateModal,
      setOpenBulkCreateModal,
    }),
    [openBulkCreateModal],
  );

  const closeModal = useCallback(() => {
    setOpenBulkCreateModal(false);
  }, []);

  if (!openBulkCreateModal) return;

  return openBulkCreateModal && <ModalCreateBulk module={module} closeModal={closeModal} />;
});

export const ModalUpdateBulkWrapper = forwardRef(function ModalUpdateBulkWrapper(
  { module, selectedIds },
  ref,
) {
  const [openBulkUpdateModal, setOpenBulkUpdateModal] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      openBulkUpdateModal,
      setOpenBulkUpdateModal,
    }),
    [openBulkUpdateModal],
  );

  const closeModal = useCallback(() => {
    setOpenBulkUpdateModal(false);
  }, []);

  if (!openBulkUpdateModal) return;

  return (
    openBulkUpdateModal && (
      <ModalUpdateBulk module={module} ids={selectedIds} closeModal={closeModal} />
    )
  );
});

export const ModalCreateBulk = ({ module, closeModal }) => {
  const { singleCreatePageUrl, verboseName, verboseNamePlural } =
    BULK_OPERATION_CREATE_CONFIG[module];

  const navigate = useNavigate();

  const notify = useNotification();

  const [triggerDownload, { error, isFetching }] = useLazyDownloadBulkFormatCreateQuery();

  const toSingleCreatePage = () => {
    if (
      module === "stock_adjustment" ||
      module === "stock_movement" ||
      module === "stock_reclassification"
    ) {
      navigate(`/inventory/${singleCreatePageUrl}`);
      return;
    }
    navigate(`/master/${singleCreatePageUrl}`);
  };

  const doDownloadFormat = () => {
    const filePrefix = module === "warehouse" ? "facility" : module;
    const fileName = `${filePrefix}_template_${new Date().toISOString().slice(0, 19).replace(/[-T:]/g, "")}.xlsx`;
    triggerDownload({ module, fileName });
  };

  useEffect(() => {
    if (error) {
      notify({
        type: "error",
        message: error?.message || "Failed to download format. Something went wrong.",
      });
    }
  }, [error, notify]);

  return (
    <ModalOnPortal>
      <ModalOverlay aria-modal="true" role="dialog" aria-labelledby="bulk-create-title">
        <ModalT1 $minWidth={480} $maxWidth={600}>
          <PortalHeader title={`Create ${verboseNamePlural}`} onClose={closeModal} />
          <ModalBodyT1 className="bulk">
            <Card className="bulk">
              <SectionTitle title="Individual" />
              <SectionDescription desc={`Add one ${verboseName} at a time`} />
              <ButtonT1
                icon="plus"
                title={`Add Single ${verboseName}`}
                onClickCallback={toSingleCreatePage}
                $variant="primaryLight"
              />
            </Card>
            <Card className="bulk">
              <SectionTitle title="Bulk Upload" />
              <SectionDescription desc={`Add multiple ${verboseNamePlural} in a single click`} />
              <ButtonT1
                disabled={isFetching}
                icon="download"
                loading={isFetching}
                title="Download Template"
                loadingText="Preparing..."
                onClickCallback={doDownloadFormat}
                $variant="primaryLight"
              />
              <BulkUploaderForm module={module} />
            </Card>
          </ModalBodyT1>
        </ModalT1>
      </ModalOverlay>
    </ModalOnPortal>
  );
};

export const ModalUpdateBulk = ({ module, closeModal, ids }) => {
  const {
    verboseName,
    verboseNamePlural,
    singleCreatePageUrl,
    updateModalTitle,
    updateSingleActionTitle,
    updateSingleDescription,
    updateMultipleDescription,
  } = BULK_OPERATION_CREATE_CONFIG[module];

  const modalTitle = updateModalTitle || `Update ${verboseNamePlural}`;
  const individualActionTitle = updateSingleActionTitle || `Update ${verboseName}`;
  const individualDescription = updateSingleDescription || `Update ${verboseName} at a time`;

  const singleIndividualDescription =
    updateSingleDescription || `Update one ${verboseName} at a time`;

  const bulkDescription =
    updateMultipleDescription || `Update multiple ${verboseNamePlural} in a single click`;

  const navigate = useNavigate();

  const notify = useNotification();

  const [triggerDownload, { error, isFetching }] = useLazyDownloadBulkFormatUpdateQuery();

  const selectionRequiredMessage = BULK_UPDATE_REQUIRES_SELECTION[module];
  const hasRequiredSelection = !selectionRequiredMessage || ids?.length > 0;

  const toSingleUpdatePage = () => {
    if (["stock_movement", "stock_reclassification"].includes(module)) {
      navigate(`/inventory/${singleCreatePageUrl}`, {
        state: { selectedIds: ids },
      });
      return;
    }
    navigate(`/master/${module}/update/${ids[0]}`);
  };

  const doDownloadFormat = () => {
    if (!hasRequiredSelection) {
      notify({
        type: "error",
        message: selectionRequiredMessage,
      });
      return;
    }

    const fileName = `${module}_template_${new Date().toISOString().slice(0, 19).replace(/[-T:]/g, "")}.xlsx`;
    triggerDownload({ module, fileName, ids });
  };

  useEffect(() => {
    if (error) {
      notify({
        type: "error",
        message: error?.message || "Failed to download format. Something went wrong.",
      });
    }
  }, [error, notify]);

  return (
    <ModalOnPortal>
      <ModalOverlay aria-modal="true" role="dialog" aria-labelledby="bulk-create-title">
        <ModalT1 $minWidth={480} $maxWidth={600}>
          <PortalHeader title={modalTitle} onClose={closeModal} />
          <ModalBodyT1 className="bulk">
            {ids?.length > 0 && ["stock_movement", "stock_reclassification"]?.includes(module) ? (
              <Card className="bulk">
                <SectionTitle title="Individual" />
                <SectionDescription desc={individualDescription} />
                <ButtonT1
                  icon="plus"
                  title={individualActionTitle}
                  onClickCallback={toSingleUpdatePage}
                  $variant="primaryLight"
                />
              </Card>
            ) : (
              ids?.length === 1 && (
                <Card className="bulk">
                  <SectionTitle title="Individual" />
                  <SectionDescription desc={singleIndividualDescription} />
                  <ButtonT1
                    icon="plus"
                    title={`Update Single ${verboseName}`}
                    onClickCallback={toSingleUpdatePage}
                    $variant="primaryLight"
                  />
                </Card>
              )
            )}
            <Card className="bulk">
              <SectionTitle title="Bulk Upload" />
              <SectionDescription desc={bulkDescription} />
              <ButtonT1
                disabled={isFetching || !hasRequiredSelection}
                icon="download"
                loading={isFetching}
                title="Download Template"
                loadingText="Preparing..."
                onClickCallback={doDownloadFormat}
                $variant="primaryLight"
              />
              {!hasRequiredSelection && selectionRequiredMessage && (
                <SectionDescription desc={selectionRequiredMessage} />
              )}
              <BulkUploaderForm module={module} mode="update" />
            </Card>
          </ModalBodyT1>
        </ModalT1>
      </ModalOverlay>
    </ModalOnPortal>
  );
};

export const PopConfirmModal = ({
  closeModal,
  onSubmit,
  message = "",
  headerTitle = "Are you sure?",
  isDisabled = false,
  applyButtonTitle = "Submit",
  ...props
}) => {
  return (
    <ModalOnPortal>
      <ModalOverlay aria-modal="true" role="dialog" aria-labelledby="Confirmation">
        <ModalT1 $minWidth={175} $maxWidth={480}>
          <PortalHeader title={headerTitle} onClose={closeModal} />
          <ModalBodyT1 className="confirm-modal-msg">
            <SectionDescription className="pop-confirm-modal" desc={message}></SectionDescription>
          </ModalBodyT1>
          <PortalFooter
            applyTitle={applyButtonTitle}
            isDisabled={isDisabled}
            onApply={onSubmit}
            onCancel={closeModal}
          />
        </ModalT1>
      </ModalOverlay>
    </ModalOnPortal>
  );
};
